import {
	ComIbmCommerceRestMemberHandlerPersonCheckoutProfileHandlerCheckoutProfileIdContainer,
	ComIbmCommerceRestMemberHandlerPersonCheckoutProfileHandlerUserIdContainer,
	PersonCheckoutProfile,
	PersonCheckoutProfileUpdate,
	PersonCheckoutProfileUpdateById,
} from './data-contracts';
import { ContentType, HttpClient, RequestParams, TraceDataType } from './http-client';

import { loggerCan } from '@/data/utils/loggerUtil';
import { logger } from '@/logging/logger';

export class CheckoutProfile<SecurityDataType = unknown> {
	http: HttpClient<SecurityDataType>;

	constructor(http: HttpClient<SecurityDataType>) {
		this.http = http;
	}

	/**
	 * @description Gets the Check Out Profiles for the logged in user using the Member service. The shopper must log in before invoking this method.
	 *
	 * @tags Checkout Profile
	 * @name CheckoutProfileGetCheckoutProfile
	 * @summary Get check out profiles
	 * @request GET:/store/{storeId}/person/@self/checkoutProfile
	 * @secure
	 * @response `200` `PersonCheckoutProfile` The requested completed successfully.
	 * @response `400` `void` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `404` `void` The specified resource could not be found.
	 * @response `500` `void` Internal server error. For details, see the server log files.
	 */
	checkoutProfileGetCheckoutProfile = (
		storeId: string,
		query?: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['checkoutProfileGetCheckoutProfile'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/person/@self/checkoutProfile`,
				params: _params,
				query: query,
				methodName: 'checkoutProfileGetCheckoutProfile',
			};
			logger.trace(traceData);
		}
		return this.http.request<PersonCheckoutProfile, void>({
			path: `/store/${storeId}/person/@self/checkoutProfile`,
			method: 'GET',
			query: query,
			secure: true,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * @description This is a deprecated service and only useful for a single checkout profile. It updates and creates the CheckOutProfile (default) for the logged in user using the Member service. The shopper must log in before invoking this method.
	 *
	 * @tags Checkout Profile
	 * @name CheckoutProfileUpdateCheckoutProfile
	 * @summary Update check out profile
	 * @request PUT:/store/{storeId}/person/@self/checkoutProfile
	 * @deprecated
	 * @secure
	 * @response `200` `ComIbmCommerceRestMemberHandlerPersonCheckoutProfileHandlerUserIdContainer` The requested completed successfully.
	 * @response `400` `void` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `404` `void` The specified resource could not be found.
	 * @response `500` `void` Internal server error. For details, see the server log files.
	 */
	checkoutProfileUpdateCheckoutProfile = (
		storeId: string,
		query?: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
		},
		data?: PersonCheckoutProfileUpdate,
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['checkoutProfileUpdateCheckoutProfile'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/person/@self/checkoutProfile`,
				params: _params,
				query: query,
				body: data,
				methodName: 'checkoutProfileUpdateCheckoutProfile',
			};
			logger.trace(traceData);
		}
		return this.http.request<
			ComIbmCommerceRestMemberHandlerPersonCheckoutProfileHandlerUserIdContainer,
			void
		>({
			path: `/store/${storeId}/person/@self/checkoutProfile`,
			method: 'PUT',
			query: query,
			body: data,
			secure: true,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * @description Creates Check Out Profile for the logged in user using the Member service. The shopper must log in before invoking this method.
	 *
	 * @tags Checkout Profile
	 * @name CheckoutProfileCreateCheckoutProfile
	 * @summary Create a check out profile
	 * @request POST:/store/{storeId}/person/@self/checkoutProfile
	 * @secure
	 * @response `201` `ComIbmCommerceRestMemberHandlerPersonCheckoutProfileHandlerCheckoutProfileIdContainer` The requested completed successfully.
	 * @response `400` `void` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `404` `void` The specified resource could not be found.
	 * @response `500` `void` Internal server error. For details, see the server log files.
	 */
	checkoutProfileCreateCheckoutProfile = (
		storeId: string,
		query?: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
		},
		data?: PersonCheckoutProfileUpdateById,
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['checkoutProfileCreateCheckoutProfile'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/person/@self/checkoutProfile`,
				params: _params,
				query: query,
				body: data,
				methodName: 'checkoutProfileCreateCheckoutProfile',
			};
			logger.trace(traceData);
		}
		return this.http.request<
			ComIbmCommerceRestMemberHandlerPersonCheckoutProfileHandlerCheckoutProfileIdContainer,
			void
		>({
			path: `/store/${storeId}/person/@self/checkoutProfile`,
			method: 'POST',
			query: query,
			body: data,
			secure: true,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * @description Updates the CheckOutProfile for the logged in user using the Member service. The shopper must log in before invoking this method.
	 *
	 * @tags Checkout Profile
	 * @name CheckoutProfileUpdateCheckoutProfileById
	 * @summary Update a check out profile
	 * @request PUT:/store/{storeId}/person/checkoutProfile/{checkoutProfileId}
	 * @secure
	 * @response `200` `ComIbmCommerceRestMemberHandlerPersonCheckoutProfileHandlerCheckoutProfileIdContainer` The requested completed successfully.
	 * @response `400` `void` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `404` `void` The specified resource could not be found.
	 * @response `500` `void` Internal server error. For details, see the server log files.
	 */
	checkoutProfileUpdateCheckoutProfileById = (
		storeId: string,
		checkoutProfileId: string,
		query?: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
		},
		data?: PersonCheckoutProfileUpdateById,
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails ||
				this.http.traceDetails['checkoutProfileUpdateCheckoutProfileById'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/person/checkoutProfile/${checkoutProfileId}`,
				params: _params,
				query: query,
				body: data,
				methodName: 'checkoutProfileUpdateCheckoutProfileById',
			};
			logger.trace(traceData);
		}
		return this.http.request<
			ComIbmCommerceRestMemberHandlerPersonCheckoutProfileHandlerCheckoutProfileIdContainer,
			void
		>({
			path: `/store/${storeId}/person/checkoutProfile/${checkoutProfileId}`,
			method: 'PUT',
			query: query,
			body: data,
			secure: true,
			type: params.type ?? ContentType.Json,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * @description Gets a Check Out Profile for the logged in user using the Member service. The shopper must log in before invoking this method.
	 *
	 * @tags Checkout Profile
	 * @name CheckoutProfileGetByIdCheckoutProfile
	 * @summary Get a check out profile
	 * @request GET:/store/{storeId}/person/checkoutProfile/{checkoutProfileId}
	 * @secure
	 * @response `200` `PersonCheckoutProfile` The requested completed successfully.
	 * @response `400` `void` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `404` `void` The specified resource could not be found.
	 * @response `500` `void` Internal server error. For details, see the server log files.
	 */
	checkoutProfileGetByIdCheckoutProfile = (
		storeId: string,
		checkoutProfileId: string,
		query?: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['checkoutProfileGetByIdCheckoutProfile'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/person/checkoutProfile/${checkoutProfileId}`,
				params: _params,
				query: query,
				methodName: 'checkoutProfileGetByIdCheckoutProfile',
			};
			logger.trace(traceData);
		}
		return this.http.request<PersonCheckoutProfile, void>({
			path: `/store/${storeId}/person/checkoutProfile/${checkoutProfileId}`,
			method: 'GET',
			query: query,
			secure: true,
			format: params.format ?? 'json',
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
	/**
	 * @description Deletes a Check Out Profile for the logged in user using the Member service. The shopper must log in before invoking this method.
	 *
	 * @tags Checkout Profile
	 * @name CheckoutProfileDeleteCheckoutProfile
	 * @summary Delete a check out profile
	 * @request DELETE:/store/{storeId}/person/checkoutProfile/{checkoutProfileId}
	 * @secure
	 * @response `204` `void` The requested completed successfully.
	 * @response `400` `void` Bad request. The request could not be understood by the server due to malformed syntax.
	 * @response `401` `void` Not authenticated. The user session is not valid.
	 * @response `403` `void` The user is not authorized to perform the specified request.
	 * @response `404` `void` The specified resource could not be found.
	 * @response `500` `void` Internal server error. For details, see the server log files.
	 */
	checkoutProfileDeleteCheckoutProfile = (
		storeId: string,
		checkoutProfileId: string,
		query?: {
			/** The response format. Valid values are json and xml. If the request contains an input body, it must use the format specified in responseFormat. If the responseFormat is not specified, the accept  HTTP header determines the format of the response. If the accept  HTTP header is not specified then default response format is json. */
			responseFormat?: 'xml' | 'json';
		},
		params: RequestParams = {}
	) => {
		const { _requestId: requestId, skipErrorLogging, ..._params } = params as any;
		let traceData: TraceDataType | undefined;

		if (
			loggerCan('trace') &&
			(!this.http.traceDetails || this.http.traceDetails['checkoutProfileDeleteCheckoutProfile'])
		) {
			traceData = {
				requestId,
				msg: 'API request',
				url: `${this.http.baseUrl}/store/${storeId}/person/checkoutProfile/${checkoutProfileId}`,
				params: _params,
				query: query,
				methodName: 'checkoutProfileDeleteCheckoutProfile',
			};
			logger.trace(traceData);
		}
		return this.http.request<void, void>({
			path: `/store/${storeId}/person/checkoutProfile/${checkoutProfileId}`,
			method: 'DELETE',
			query: query,
			secure: true,
			storeId,
			traceData,
			skipErrorLogging,
			..._params,
		});
	};
}
